import { invoiceService } from "../invoiceService";
import { paymentService } from "../paymentService";

const EXCLUDED_STATUSES = new Set([
  "void",
  "voided",
  "canceled",
  "cancelled",
  "uncollectible",
  "deleted",
  "refunded",
]);

const DRAFT_STATUSES = new Set(["draft"]);

function requireOrganizationId(organizationId) {
  if (!organizationId) {
    throw new Error("Select a business to view its invoices.");
  }
}

function amountValue(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function invoiceStatus(invoice) {
  const status = String(invoice?.status || "draft").toLowerCase();

  if (
    EXCLUDED_STATUSES.has(status) ||
    DRAFT_STATUSES.has(status) ||
    status === "paid"
  ) {
    return status;
  }

  const amountDue = amountValue(invoice?.amount_due);
  const today = new Date().toISOString().slice(0, 10);

  if (
    invoice?.due_date &&
    invoice.due_date < today &&
    amountDue !== null &&
    amountDue > 0
  ) {
    return "overdue";
  }

  if (
    amountDue !== null &&
    amountDue > 0 &&
    amountValue(invoice?.amount_paid) > 0
  ) {
    return "partial";
  }

  return status;
}

function createSummary(invoices) {
  const summary = {
    totalInvoices: invoices.length,
    totalInvoiced: 0,
    outstanding: 0,
    overdue: 0,
  };

  const today = new Date().toISOString().slice(0, 10);

  invoices.forEach((invoice) => {
    const status = invoiceStatus(invoice);

    if (EXCLUDED_STATUSES.has(status) || DRAFT_STATUSES.has(status)) {
      return;
    }

    const total = amountValue(invoice.total_amount);
    const due = amountValue(invoice.amount_due);

    if (total !== null) summary.totalInvoiced += total;

    if (
      due !== null &&
      due > 0 &&
      status !== "paid"
    ) {
      summary.outstanding += due;

      if (invoice.due_date && invoice.due_date < today) {
        summary.overdue += due;
      }
    }
  });

  return summary;
}

export const clientInvoiceService = {
  async getClientInvoices({ organizationId }) {
    requireOrganizationId(organizationId);

    const invoices = await invoiceService.getInvoices(organizationId);

    return {
      invoices,
      summary: createSummary(invoices),
    };
  },

  async getClientInvoiceDetails({ organizationId, invoiceId }) {
    requireOrganizationId(organizationId);

    if (!invoiceId) {
      throw new Error("Invoice ID is required.");
    }

    const invoice = await invoiceService.getInvoiceById(
      organizationId,
      invoiceId,
    );

    let payments = [];
    let paymentHistoryUnavailable = false;

    try {
      const allPayments = await paymentService.getPayments(organizationId);

      payments = (allPayments || [])
        .map((payment) => {
          const allocations = (payment.allocations || []).filter(
            (allocation) => allocation.invoice_id === invoiceId,
          );

          if (!allocations.length) return null;

          return {
            ...payment,
            invoiceAllocations: allocations,
          };
        })
        .filter(Boolean);
    } catch (error) {
      console.warn(
        "[Client Invoices] Payment history is unavailable:",
        error,
      );
      paymentHistoryUnavailable = true;
    }

    return {
      ...invoice,
      payments,
      paymentHistoryUnavailable,
    };
  },
};

export { invoiceStatus };